"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, ArrowRight, Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { FormProvider } from "@/integrations/forms/FormProvider";
interface Post {
  id: string;
  title: string;
  slug?: string;
  excerpt?: string;
  content?: string;
  link?: string;
  date?: string;
  featuredImage?: string;
}
function BlogPostsConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-[16/9] bg-muted animate-pulse" />
            <div className="h-6 bg-muted animate-pulse w-3/4" />
            <div className="h-4 bg-muted animate-pulse w-full" />
            <div className="h-4 bg-muted animate-pulse w-2/3" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="p-8 border border-destructive/50 bg-destructive/10 text-destructive">
        <p>Impossibile caricare gli articoli. Riprova più tardi.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="p-8 border border-border bg-muted/20 text-muted-foreground text-center">
        <p>Nessun articolo trovato.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {posts.map((post: Post, i: number) => <article key={post.id} data-index={i} className="group flex flex-col border border-border bg-card hover:border-primary/50 transition-colors duration-300">
            <Link to={post.link || "#"} className="block relative aspect-[16/9] overflow-hidden bg-primary">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] bg-[size:20px_20px] z-10 pointer-events-none"></div>
              
              <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-primary-foreground/60 z-10"></div>
              <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-primary-foreground/60 z-10"></div>
              
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585744/img/generic_blueprint_post.png"} alt={post.title} className="object-cover w-full h-full mix-blend-luminosity opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
            </Link>
            
            <div className="p-6 flex flex-col flex-grow">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3 font-mono uppercase tracking-wider">
                <SafeHydrate><Calendar className="w-3.5 h-3.5" /></SafeHydrate>
                <time dateTime={post.date}>{post.date ? new Date(post.date).toLocaleDateString('it-IT') : "Data non disponibile"}</time>
              </div>
              
              <h3 className="text-xl font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                <Link to={post.link || "#"}>{post.title}</Link>
              </h3>
              
              <div className="text-muted-foreground text-sm mb-6 line-clamp-3 flex-grow" dangerouslySetInnerHTML={{
            __html: post.excerpt || ""
          }} />
              
              <div className="mt-auto pt-4 border-t border-border/50">
                <Link to={post.link || "#"} className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors uppercase tracking-wide">
                  Leggi di più
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </article>)}
      </div>

      {totalPages > 1 && <Pagination className="border-t border-border pt-8">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious href="#" onClick={e => {
            e.preventDefault();
            if (hasPrev) prevPage();
          }} className={!hasPrev ? "pointer-events-none opacity-50" : ""} />
            </PaginationItem>
            <PaginationItem>
              <span className="text-sm font-mono text-muted-foreground px-4">
                PAGINA {currentPage} DI {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext href="#" onClick={e => {
            e.preventDefault();
            if (hasNext) nextPage();
          }} className={!hasNext ? "pointer-events-none opacity-50" : ""} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
function PopularPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-4">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex gap-4">
            <div className="w-20 h-20 bg-muted animate-pulse shrink-0" />
            <div className="flex-grow space-y-2 py-1">
              <div className="h-4 bg-muted animate-pulse w-full" />
              <div className="h-4 bg-muted animate-pulse w-2/3" />
            </div>
          </div>)}
      </div>;
  }
  if (error || posts.length === 0) return null;
  return <div className="space-y-6">
      {posts.slice(0, 4).map((post: Post, i: number) => <Link key={post.id} to={post.link || "#"} data-index={i} className="group flex gap-4 items-start">
          <div className="w-20 h-20 shrink-0 bg-primary relative overflow-hidden border border-border">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] bg-[size:10px_10px] z-10 pointer-events-none"></div>
            <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585744/img/generic_blueprint_thumb.png"} alt="" className="w-full h-full object-cover mix-blend-luminosity opacity-60 group-hover:opacity-90 group-hover:scale-110 transition-all duration-300" />
          </div>
          <div className="flex flex-col justify-center min-h-[5rem]">
            <h4 className="text-sm font-bold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
              {post.title}
            </h4>
            <span className="text-xs text-muted-foreground mt-2 font-mono uppercase">
              {post.date ? new Date(post.date).toLocaleDateString('it-IT') : "Recente"}
            </span>
          </div>
        </Link>)}
    </div>;
}
function NewsletterForm() {
  const [email, setEmail] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setEmail("");
    setTimeout(() => setSubmitted(false), 3000);
  };
  return <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="space-y-2">
        <label htmlFor="email-subscribe" className="sr-only">Indirizzo Email</label>
        <input id="email-subscribe" type="email" placeholder="Il tuo indirizzo email" value={email} onChange={e => setEmail(e.target.value)} className="flex h-10 w-full border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors" required />
      </div>
      <Button type="submit" className="w-full uppercase tracking-wider font-bold">
        {submitted ? "Iscritto!" : "Iscriviti Ora"}
      </Button>
      <p className="text-xs text-muted-foreground text-center mt-2">
        Rispettiamo la tua privacy. Nessun costo, cancellazione in qualsiasi momento.
      </p>
    </form>;
}
export default function RecentArticles() {
  const mainQueryParams = {
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const mainQuery = new WP_Query(mainQueryParams);
  const popularQueryParams = {
    per_page: 4,
    order: "desc",
    orderby: "date"
  };
  const popularQuery = new WP_Query(popularQueryParams);
  return <section id="articoli-recenti" className="bg-background py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)] opacity-20 pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
            Aggiornamenti e <span className="text-primary">Approfondimenti</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Rimani informato sulle ultime novità normative, approfondimenti tecnici e best practice nel campo della sicurezza sul lavoro e dell'ingegneria.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <PostsProvider wp_query={{
            per_page: 6,
            order: "desc",
            orderby: "date"
          }}>
              <BlogPostsConsumer />
            </PostsProvider>
          </div>

          <aside className="lg:col-span-4 space-y-12">
            <Card className="border-border bg-card/50 backdrop-blur-sm shadow-none">
              <CardHeader className="border-b border-border/50 pb-4 mb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-bold uppercase tracking-wider text-foreground">
                  <Mail className="w-5 h-5 text-primary" />
                  Iscriviti alla Newsletter
                </CardTitle>
                <CardDescription className="text-sm">
                  Ricevi aggiornamenti normativi e consigli tecnici direttamente nella tua casella di posta.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider>
                  <NewsletterForm />
                </FormProvider>
              </CardContent>
            </Card>

            <div className="border border-border bg-card/50 backdrop-blur-sm p-6">
              <h3 className="text-lg font-bold uppercase tracking-wider text-foreground mb-6 pb-4 border-b border-border/50 flex items-center gap-2">
                <span className="w-2 h-2 bg-primary inline-block"></span>
                Post Popolari
              </h3>
              <PostsProvider wp_query={{
              per_page: 4,
              order: "desc",
              orderby: "date"
            }}>
                <PopularPostsConsumer />
              </PostsProvider>
            </div>
            
            <div className="bg-primary text-primary-foreground p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] bg-[size:20px_20px] z-0 pointer-events-none"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-bold mb-3">Hai bisogno di consulenza?</h3>
                <p className="text-primary-foreground/80 text-sm mb-6">
                  I nostri ingegneri sono a disposizione per valutare le tue esigenze di sicurezza e progettazione.
                </p>
                <Link to="#contatti">
                  <Button variant="outline" className="w-full bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    Contattaci Subito
                  </Button>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}